import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { searchRoster } from '../../../lib/occupants';
import { dinnerPickerEmptyNote } from '../../../lib/dinnerGuests';
import type { GroupDinnerClaim, GroupInvitee } from '../../../types/groups';

/** Enough to recognise the right Sarah; more than that means keep typing. */
const MAX_RESULTS = 8;

export interface NewDinnerGuest {
  fullName: string;
  email: string | null;
  phone: string | null;
}

/**
 * Who the host wants to seat at this table: someone already on the trip, or
 * someone who is about to be.
 *
 * Deliberately shaped like the room-occupant picker — search first, and the
 * "add someone new" form reached THROUGH the search — because the ordering is
 * the duplicate guard. A host about to type a name that already exists sees it
 * on the roster before they can make a second copy of them.
 *
 * What this does NOT do is pre-filter guests who are booked at another table
 * that evening. The server owns the one-table-per-night rule; a copy of it here
 * would be a second rule that can disagree, and a guest silently missing from a
 * picker explains nothing. They stay in the list, the claim is refused, and the
 * refusal names the other venue.
 */
export default function DinnerGuestPicker({
  roster,
  claims,
  saving,
  onSeatInvitee,
  onAddGuest,
}: {
  roster: GroupInvitee[];
  /** Who is already at this table. Every entry is capacity-reducing. */
  claims: GroupDinnerClaim[];
  saving: boolean;
  onSeatInvitee: (invitee: GroupInvitee) => void;
  onAddGuest: (guest: NewDinnerGuest) => void;
}) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const matches = useMemo(() => searchRoster(roster, claims, query), [roster, claims, query]);
  const shown = matches.slice(0, MAX_RESULTS);
  const seatedFromRoster = claims.filter((claim) => claim.inviteeId).length;

  const openCreate = () => {
    // Carry the query across as the name: the host has already typed it once and
    // watched the roster fail to offer it.
    setFullName(query.trim());
    setCreating(true);
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        fullWidth
        label="Add a guest to this table"
        placeholder="Search guests on this trip by name, email or phone"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {shown.length > 0 ? (
        <Stack spacing={0.5}>
          {shown.map((invitee) => (
            <Box
              key={invitee.id}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>{invitee.fullName}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {invitee.email || invitee.phone || 'No email or phone on file — they can’t claim this themselves'}
                </Typography>
              </Box>
              <Button size="small" disabled={saving} onClick={() => onSeatInvitee(invitee)}>
                Save a seat
              </Button>
            </Box>
          ))}
          {matches.length > shown.length ? (
            <Typography variant="caption" color="text.secondary">
              Showing {shown.length} of {matches.length} — keep typing to narrow it down.
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {dinnerPickerEmptyNote(roster, seatedFromRoster, query)}
        </Typography>
      )}

      {creating ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" fontWeight={700}>Add a guest who isn’t on the trip yet</Typography>
            {/*
              Says out loud that this writes to the roster. It is the correct
              write — they are attending, so they belong on the list of people
              attending — but a host who thinks they are only touching a dinner
              would be surprised to find a new guest on the Guests tab.
            */}
            <Typography variant="caption" color="text.secondary">
              This adds them to the trip’s guest list first, then saves their seat. It sends nothing.
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                helperText="If this address is already on the trip we’ll seat that guest instead of adding a second copy."
              />
              <TextField
                size="small"
                fullWidth
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setCreating(false)}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                disabled={saving || !fullName.trim()}
                onClick={() =>
                  onAddGuest({
                    fullName: fullName.trim(),
                    email: email.trim() || null,
                    phone: phone.trim() || null,
                  })
                }
              >
                {saving ? 'Saving…' : 'Add to trip and save a seat'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <Box>
          <Button size="small" disabled={saving} onClick={openCreate}>
            {query.trim() ? `Add “${query.trim()}” as a new guest` : 'Add a guest who isn’t on the trip yet'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
